// Shared imports
const nameUtils = require('shared/name-utils')
const sharedUtils = require('shared/utils')
// Client V1 imports
const airbrake = require('client/lib/airbrake')
const businessEvents = require('client/lib/business-events')
const { send: ajax } = require('client/lib/request')
// Client V2 common imports
const userModelActions = require('client-v2/common/models/user/actions')
const routerActions = require('client-v2/common/state/router/actions')
const { FetchStateEnum } = require('client-v2/common/utils/enums')

// Action types

const SEARCH_FORM_RESET = 'SEARCH_FORM_RESET'
const SEARCH_FORM_SET_VALUE = 'SEARCH_FORM_SET_VALUE'
const SEARCH_FORM_SHOW = 'SEARCH_FORM_SHOW'
const SEARCH_FORM_GOOGLE_SUGGESTIONS_FAILED = 'SEARCH_FORM_GOOGLE_SUGGESTIONS_FAILED'
const SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADED = 'SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADED'
const SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADING = 'SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADING'
const SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_FAILED = 'SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_FAILED'
const SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADED = 'SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADED'
const SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADING = 'SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADING'
const SEARCH_FORM_USER_SUGGESTIONS_FAILED = 'SEARCH_FORM_USER_SUGGESTIONS_FAILED'
const SEARCH_FORM_USER_SUGGESTIONS_LOADED = 'SEARCH_FORM_USER_SUGGESTIONS_LOADED'
const SEARCH_FORM_USER_SUGGESTIONS_LOADING = 'SEARCH_FORM_USER_SUGGESTIONS_LOADING'

// Action creators

function reset() {
  return {
    type: SEARCH_FORM_RESET
  }
}

function setValue(value) {
  return {
    type: SEARCH_FORM_SET_VALUE,
    payload: value
  }
}

// Action dispatchers

function fetchSuggestions(value) {
  return async function (dispatch) {
    if (!value) {
      dispatch({
        type: SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADED,
        payload: []
      })
      dispatch({
        type: SEARCH_FORM_USER_SUGGESTIONS_LOADED,
        payload: []
      })
      return
    }
    dispatch(fetchSupersearchSuggestions())
    dispatch(fetchGoogleSuggestions(value))
    dispatch(fetchUserSuggestions(value))
  }
}

function fetchGoogleSuggestions(value) {
  return async function (dispatch) {
    dispatch({
      type: SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADING
    })
    try {
      dispatch({
        type: SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADED,
        payload: await getGoogleSuggestions(value)
      })
    } catch (err) {
      airbrake.notify(err)
      dispatch({
        type: SEARCH_FORM_GOOGLE_SUGGESTIONS_FAILED
      })
    }
  }
}

function fetchUserSuggestions(value) {
  return async function (dispatch) {
    dispatch({
      type: SEARCH_FORM_USER_SUGGESTIONS_LOADING
    })
    try {
      dispatch({
        type: SEARCH_FORM_USER_SUGGESTIONS_LOADED,
        payload: await getUserSuggestions(value)
      })
    } catch (err) {
      airbrake.notify(err)
      dispatch({
        type: SEARCH_FORM_USER_SUGGESTIONS_FAILED
      })
    }
  }
}

function fetchSupersearchSuggestions() {
  return async function (dispatch, getState) {
    const { user, searchForm } = getState()
    if (
      user.loggedIn && !user.bot && searchForm &&
      searchForm.supersearchSuggestionsState === FetchStateEnum.EMPTY
    ) {
      dispatch({
        type: SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADING
      })
      try {
        dispatch({
          type: SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADED,
          payload: await getSupersearchSuggestions()
        })
      } catch (err) {
        airbrake.notify(err)
        dispatch({
          type: SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_FAILED
        })
      }
    }
  }
}

function show() {
  return async function (dispatch) {
    dispatch({ type: SEARCH_FORM_SHOW })
    dispatch(fetchSupersearchSuggestions())
  }
}

function submit(suggestion) {
  return async function (dispatch, getState) {
    const searchFormValue = getState().searchForm.value
    if (!suggestion) {
      await businessEvents.trackSegment('search-topic', {
        text: searchFormValue
      })
      dispatch(routerActions.navigate(searchArticlesHref(searchFormValue)))
    } else {
      const { complete, type } = suggestion
      await businessEvents.trackSegment('typeahead-select', {
        type,
        text: complete,
        search: searchFormValue
      })
      if (type === 'contact') {
        dispatch(userModelActions.navigateToUserProfile({
          user: { username: complete }
        }))
      } else {
        dispatch(routerActions.navigate(searchArticlesHref(complete)))
      }
    }
  }
}

// Helpers

function searchArticlesHref(query) {
  return `/search/${encodeURIComponent(query)}`
}

async function getSupersearchSuggestions() {
  return ajax({
    url: '/api/v0.1/user/supersearch',
    method: 'post'
  })
  .catch(() => [])
}

async function getUserSuggestions(query) {
  return ajax({
    url: '/api/v0.1/users/search',
    method: 'post',
    data: { text: query }
  })
  .then(response => {
    return response.items
    .slice(0, 3)
    .map(user => ({
      complete: user.username,
      names: [user['display_name'], `@${user.username}`],
      photo: user['photo_url'],
      type: 'contact'
    }))
  })
}

async function getGoogleSuggestions(query) {
  const cleanQuery = nameUtils.displayText(query)
  const querySuggestion = {
    complete: cleanQuery,
    names: [cleanQuery],
    type: 'google'
  }
  const suggestions = [querySuggestion]
  return ajax({
    url: 'https://suggestqueries.google.com/complete/search',
    method: 'get',
    dataType: 'jsonp',
    data: {
      q: query,
      client: 'chrome'
    }
  })
  .then(response => {
    response[1]
    .filter(text => !sharedUtils.isExternalUrl(text))
    .slice(0, 6)
    .forEach(text => {
      const cleanText = nameUtils.displayText(text)
      // The original query is already at the top of the list
      if (cleanText !== cleanQuery) {
        suggestions.push({
          complete: cleanText,
          names: [cleanText],
          type: 'google'
        })
      }
    })
  })
  /**
   * Gracefully handle Google suggestqueries API errors
   * @see https://app.asana.com/0/102902769366319/690838729515894/f
   */
  .catch(err => {
    airbrake.notify(err)
  })
  .then(() => suggestions)
}

module.exports = {
  // Action types
  SEARCH_FORM_RESET,
  SEARCH_FORM_SET_VALUE,
  SEARCH_FORM_SHOW,
  SEARCH_FORM_GOOGLE_SUGGESTIONS_FAILED,
  SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADED,
  SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADING,
  SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_FAILED,
  SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADED,
  SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADING,
  SEARCH_FORM_USER_SUGGESTIONS_FAILED,
  SEARCH_FORM_USER_SUGGESTIONS_LOADED,
  SEARCH_FORM_USER_SUGGESTIONS_LOADING,
  // Action creators
  reset,
  setValue,
  // Action dispatchers
  fetchSuggestions,
  show,
  submit
}
